import editorialStyle from "../../../components/editorial/style.scss";
import expandableStyle from "../../../components/expandable/style.scss";
import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  ProductLink,
  StickyButtons,
  Button,
  ButtonList,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import NoWrap from "../../../components/no-wrap";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import ImageSet from "../../../components/image-set";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import Accordion from "../../../components/accordion";
import NoMargin from "../../../components/no-margin";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import YouTube from "../../../components/youtube";
import Warning from "../../../components/warning";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import { mergeClasses } from "../../../components/helpers";
import namedRoutes from "../../../settings/routes";

import AffiliateAd from "../../../components/affiliate-ad";

import powerStationData from "../../../data/power-stations/ecoflow";
import { getGroupedData, getDataItems } from "../../power-systems/dataHelper";
import DataView from "../../power-systems/dataView";

const groupedData = getGroupedData(powerStationData, {
  //filterFn: (item) => !item?.discontinued,
  getGroupName: (item) => {
    const dcTotal = (item.dcOutput || 0) + (item.dcOutput2 || 0);
    if (item.name.includes("Power Kit")) return "powerkits";
    if (dcTotal > 0) return "powerStationsDC";
    return "powerStationsNoDC";
  },
  groups: ["powerStationsNoDC", "powerStationsDC", "powerkits"],
});

const productsWithPowerpole = getDataItems(powerStationData, [
  "DELTA Pro 3",
  "DELTA Pro",
  "DELTA 3 Ultra Plus",
  "DELTA 3 Max Plus",
  "DELTA Pro Ultra",
]);

const productsWithCigarette = {
  systems: groupedData.powerStationsDC.systems.filter(
    (item) => item.name !== "DELTA Pro 3" && item.name !== "DELTA Pro Ultra"
  ),
  captions: groupedData.powerStationsDC.captions,
};

const headerInfo = lookupByUrl(namedRoutes.ecoflow.wiring12v);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      thumbnail={headerInfo?.og_image}
      title="EcoFlow 12V Wiring Guide"
      subTitle="Fuse Blocks, Anderson Powerpole, Cigarette‑Port & Adapters"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          Connecting a <strong>12V fuse block</strong> to your{" "}
          <strong>EcoFlow power station</strong> is the safest way to run
          multiple DC accessories. This guide covers{" "}
          <strong>Anderson Powerpole</strong> ports,{" "}
          <strong>cigarette sockets</strong>, wire sizing, and the best adapters
          for vanlife, RV, campers, overland rigs, marine boats, and off-grid
          cabins.
        </p>

        {/* <figure>
          <NoMargin>
            <YouTube id="" />
          </NoMargin>
          <figcaption>
            EcoFlow 12V Wiring Guide Article Companion Video
          </figcaption>
        </figure> */}

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>12V Wiring Diagrams</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Connecting a 12V Fuse Block</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>
                          Anderson Powerpole Port with Fuse Block
                        </SlugLink>
                      </li>
                      <li>
                        <SlugLink>12V Cigarette Port with Fuse Block</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Wiring 12V Circuits</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Adding a 12V Cigarette Port</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Adding an Anderson Powerpole Port</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Adding USB Ports</SlugLink>
                  </li>
                  <li>
                    <SlugLink>DC5521 Barrel Jack</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Powering DC Air Conditioners</SlugLink>
                  </li>
                  <li>
                    <SlugLink>EcoFlow Power Station Comparison</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">12V Wiring Diagrams</SlugHeading>
        <ImageSet
          size={2}
          images={[
            {
              lg: "/images/diagrams/wiring/ecoflow/anderson-powerpole-fuse-panel.webp?v=2",
              alt: "Connecting DC fuse block to Anderson Powerpole port on EcoFlow power station",
            },
            {
              lg: "/images/diagrams/wiring/ecoflow/cigarette-fuse-panel.webp?v=2",
              alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
            },
          ]}
        />

        <SlugHeading tag="h2">Connecting a 12V Fuse Block</SlugHeading>
        <p>
          A DC fuse block acts as a central hub to{" "}
          <strong>safely protect multiple 12V accessories</strong> running off
          your EcoFlow DELTA or RIVER solar generator. What you need depends on
          which 12V DC output port your model features:
        </p>

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Port Type</th>
                <th>Max Amps</th>
                <th>Max Power</th>
                <th>Best Use</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>Anderson Powerpole</NoWrap> Port
                  </strong>
                </th>
                <td>
                  <mark class={style.green}>30A</mark>
                </td>
                <td>
                  <mark class={style.green}>~378W</mark>
                </td>
                <td>Medium/large 12V setups (lights, fridge, diesel heater)</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>Cigarette Lighter</NoWrap> Port
                  </strong>
                </th>
                <td>10A</td>
                <td>~100W</td>
                <td>Small 12V setups (fridge, LED strips, fans)</td>
              </tr>
            </tbody>
          </table>
        </FixedTable>

        {/* <ul>
          <li>
            <strong>Anderson Powerpole port</strong> (30A)
          </li>
          <li>
            <strong>Automotive style cigarette port</strong> (10A)
          </li>
        </ul> */}

        <p class="caption-left">
          Note: A fuse box distributes the power your EcoFlow already provides.
          It cannot increase the total power available.
        </p>

        <div class={style.section}>
          <SlugHeading tag="h3">
            Anderson Powerpole Port with Fuse Block
          </SlugHeading>
          <p>
            Some EcoFlow models include a high‑current 12V output that uses{" "}
            <strong>Anderson Powerpole</strong> connectors. This connection type
            can support up to <strong>30A total (378W)</strong>, making it the
            best option for running a fuse panel. Powerpole connectors also lock
            securely and avoid the loose plug issues that are common with
            cigarette ports.
          </p>

          <ImageSet
            size={3}
            images={[
              {
                lg: "/images/diagrams/wiring/ecoflow/anderson-powerpole-fuse-panel.webp?v=2",
                alt: "Connecting DC fuse block to Anderson Powerpole port on EcoFlow power station",
              },
              // {
              //   lg: "/images/products/ecoflow/delta-pro-3/lg/1967.webp",
              //   alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
              // },
              // {
              //   lg: "/images/products/ecoflow/delta-pro-3/lg/1967.webp",
              //   alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
              // },
            ]}
          />

          <ul class="none">
            <li>
              <PhotoListItem
                affiliate="https://amzn.to/3Vto1Vq"
                href="https://www.amazon.com/dp/B001P6FTHC"
                img="/images/products/blue-sea-systems/5026.webp"
                title="DC Fuse Block"
                titleSuffix="(12 Circuits)"
                description={
                  <Fragment>
                    A 12 circuit fuse block for larger 12V setups. Provides
                    organized, individually fused outputs for multiple
                    accessories. See{" "}
                    <Link
                      affiliate="https://amzn.to/3T7RqE3"
                      href="https://www.amazon.com/dp/B000THQ0CQ"
                    >
                      6 circuit model
                    </Link>
                    .
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
            <li>
              <PhotoListItem
                affiliate="https://amzn.to/4cGXGsW"
                href="https://www.amazon.com/dp/B0B2P5JDX7"
                img="/images/products/paekq-10awg-anderson-ring.webp"
                title="Anderson Powerpole to Fuse Block"
                description={
                  <Fragment>
                    Fused 10AWG cable to connect a DC fuse block to an EcoFlow
                    power station's Anderson Powerpole port. Recommended fuse
                    size is 30A-35A (
                    <SlugLink text="Main Cable Fuse Sizing">details</SlugLink>).
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
            <li>
              <PhotoListItem
                affiliate="https://link.amazon/B089Bvfqb"
                href="https://www.amazon.com/dp/B000BXKYW0"
                img="/images/products/assorted/fuses/bussman-fuse-pack.webp"
                title="ATC Blade Fuse Assortment"
                titleSuffix="(42 Pack)"
                description={
                  <Fragment>
                    An assortment of 5A–30A ATC fuses. Always use name brands
                    like Bussmann, Littelfuse, or Blue Sea Systems as cheap
                    generic fuses can fail to blow posing a fire hazard.
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
          </ul>

          <p>
            <strong>Tip:</strong> Anderson Powerpole connectors can be slid
            apart and flipped if the orientation doesn’t match your EcoFlow.{" "}
            {/* <mark>
              TODO: Take photos of DP3 + fuse panel, Anderson connector change
            </mark> */}
          </p>

          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "EcoFlow models with an Anderson Powerpole port",
                content: (
                  <Fragment>
                    <EcoFlowCouponCode
                      href="https://us.ecoflow.com/"
                      buttonText="Shop EcoFlow"
                    />
                    <DataView data={productsWithPowerpole} sortMessage={null} />
                  </Fragment>
                ),
              },
            ]}
          />

          <p class="caption-left">
            Note: An{" "}
            <Link
              affiliate="https://amzn.to/4cGXGsW"
              href="https://www.amazon.com/dp/B0B2P5JDX7"
            >
              Anderson Powerpole connector
            </Link>{" "}
            is different from an{" "}
            <Link
              affiliate="https://link.amazon/B0c4ojyXf"
              href="https://www.amazon.com/dp/B0CQLZP7LX"
            >
              Anderson SB50 connector
            </Link>
            .
          </p>

          <AffiliateAd brand="EcoFlow" size="billboard" />
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">12V Cigarette Port with Fuse Block</SlugHeading>
          <p>
            Most EcoFlow units only provide a{" "}
            <strong>12V cigarette port</strong>. This port is limited to about{" "}
            <strong>10A total (100W)</strong>, so while it can run a small fuse
            panel, it's limited to either a single item or a few low-draw
            accessories.
          </p>

          <ImageSet
            size={3}
            images={[
              {
                lg: "/images/diagrams/wiring/ecoflow/cigarette-fuse-panel.webp?v=2",
                alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
              },
              // {
              //   lg: "/images/products/ecoflow/delta-pro-3/lg/1967.webp",
              //   alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
              // },
              // {
              //   lg: "/images/products/ecoflow/delta-pro-3/lg/1967.webp",
              //   alt: "Connecting DC fuse block to cigarette lighter port on EcoFlow power station",
              // },
            ]}
          />

          <ul class="none">
            <li>
              <PhotoListItem
                affiliate="https://amzn.to/3T7RqE3"
                href="https://www.amazon.com/dp/B000THQ0CQ"
                img="/images/products/blue-sea-systems/5025.webp"
                title="DC Fuse Block"
                titleSuffix="(6 Circuits)"
                description={
                  <Fragment>
                    A compact fuse block for small 12V systems. Ideal when you
                    only need a few fused accessory circuits.
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
            <li>
              <PhotoListItem
                affiliate="https://amzn.to/4iVHtnC"
                href="https://www.amazon.com/dp/B0B2F9S698"
                img="/images/products/assorted/cables/cigarette-plug-to-ring-terminals.webp"
                title="Cigarette port to fuse panel"
                description={
                  <Fragment>
                    Fused 12AWG cable to connect a DC fuse block to an EcoFlow
                    power station's cigarette port. Recommended fuse size is
                    10A-15A (
                    <SlugLink text="Main Cable Fuse Sizing">details</SlugLink>).
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
            <li>
              <PhotoListItem
                affiliate="https://link.amazon/B0i23OwgQ"
                href="https://www.amazon.com/dp/B000MMFJFM"
                img="/images/products/assorted/fuses/blue-sea-fuses.webp"
                title="ATC Blade Fuses"
                description={
                  <Fragment>
                    Individually selected ATC fuses. Always use name brands like
                    Blue Sea Systems, Bussmann, or Littelfuse as cheap generic
                    fuses can fail to blow posing a fire hazard.
                  </Fragment>
                }
                buttonText="Amazon"
                buttonIcon="Amazon"
              />
            </li>
          </ul>

          <p>
            <strong>Tip:</strong> Cigarette plugs can loosen from road
            vibrations. Periodically check that the plug is fully seated.
            Wrapping a rubber band around the plug body adds friction, which
            helps prevent it from backing out.
            {/* <mark>
              TODO: Take photos of D3M + fuse panel, Rubber band trick
            </mark> */}
          </p>

          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "EcoFlow models with a 12V cigarette port",
                content: (
                  <Fragment>
                    <EcoFlowCouponCode
                      href="https://us.ecoflow.com/"
                      buttonText="Shop EcoFlow"
                    />
                    <DataView data={productsWithCigarette} sortMessage={null} />
                  </Fragment>
                ),
              },
            ]}
          />

          <p class="caption-left">
            Note: There are a few EcoFlow power stations that don't include a
            cigarette port (
            <SlugLink text="EcoFlow Power Station Comparison">
              see below
            </SlugLink>
            ). 12V cigarette ports are also known as a 12V socket, car power
            outlet, or cigarette lighter port.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Wiring 12V Circuits</SlugHeading>
          <p>
            Connecting accessories to a DC fuse block is straightforward, but
            choosing the right wire gauge, fuse size, and termination method is
            critical to prevent voltage drop and fire hazards.
          </p>
          <ul>
            <li>
              <strong>Wire Sizing:</strong> Size your wire based on the
              accessory's amp draw and total round-trip length (from the fuse
              block to the device and back). Stranded marine-grade copper wire
              is recommended for automotive applications like campers to
              withstand ongoing vibration. For guidance on calculating wire size
              (AWG) and voltage drop, refer to Blue Sea Systems:{" "}
              <Link
                external
                href="https://www.bluesea.com/support/articles/Circuit_Protection/1437/Part_1%3A_Choosing_the_Correct_Wire_Size_for_a_DC_Circuit"
              >
                Choosing the Correct Wire Size for a DC Circuit
              </Link>
              .
            </li>
            <li>
              <strong>Fuse Sizing:</strong> Remember that fuses protect the
              wire, not the device. Your fuse rating must be higher than the
              device's continuous current (typically 125% of the amp rating) but
              never exceed the maximum current capacity of the wire. Standard
              ATC blade fuses are recommended for most 12V fuse box setups. For
              a step-by-step fuse sizing breakdown, see Blue Sea Systems:{" "}
              <Link
                external
                href="https://www.bluesea.com/support/articles/Circuit_Protection/1441/Part_2%3A_Select_a_Fuse_and_Fuse_Holder_For_Your_DC_Product_Installation"
              >
                Select a Fuse and Fuse Holder For Your DC Product Installation
              </Link>
              .
            </li>
            <li id="main-cable-fuse-sizing">
              <strong>Main Cable Fuse Sizing:</strong> The main inline fuse
              protects the 10AWG or 12AWG cable connecting your EcoFlow to the
              fuse block. While the power station's port output is
              electronically capped at 30A (Anderson) or 10A (cigarette), sizing
              the inline fuse slightly higher (35A or 15A) prevents thermal fuse
              fatigue under high continuous loads while keeping the wire safely
              protected.
            </li>
            <li>
              <strong>Crimping Ring Terminals:</strong> Use high-quality
              nylon-insulated or heat-shrink ring terminals sized for your fuse
              block screws. Crimp using a dedicated ratcheting crimper for a
              proper connection, then seal heat-shrink sleeves to keep out
              moisture.
            </li>
            <li>
              <strong>Polarity & Grounding:</strong> Connect positive (
              <NoWrap>+ red</NoWrap>) wires to the fused screws and negative (
              <NoWrap>- black</NoWrap>) wires directly to the block's negative
              bus bar. Run all positive and negative circuits directly to the
              fuse block; never ground the negative side of a portable power
              station setup to your vehicle's chassis.
            </li>
          </ul>
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Adding a 12V Cigarette Port</SlugHeading>
        <p>
          Sometimes your EcoFlow doesn’t include a built‑in cigarette port, or
          you simply need more than one. You can add a cigarette port in a few
          different ways depending on what outputs your power station already
          has.
        </p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/4yECpJ6"
              href="https://www.amazon.com/dp/B086PLLQW2"
              img="/images/products/assorted/cables/anderson-powerpole-to-cigarette-port.webp"
              title="Powerpole to Cigarette Port Adapter"
              description={
                <Fragment>
                  If your EcoFlow has a high‑current Anderson Powerpole, you can
                  add a cigarette port using a simple adapter. This gives you a
                  standard 12V socket even on models that don’t include one
                  (DELTA Pro 3, DELTA Pro Ultra).
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/4xSFrt0"
              href="https://www.amazon.com/dp/B00G8WLW2Y"
              img="/images/products/assorted/cables/noco_ring-terminals-to-cigarette-port.webp"
              title="Fuse Panel to Cigarette Port Adapter"
              description={
                <Fragment>
                  If you’re already using a DC fuse panel, you can add one or
                  more cigarette ports directly from the fuse block. This gives
                  you multiple clean, fused 12V sockets.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/4cGRcuf"
              href="https://www.amazon.com/dp/B00G8WLT7C"
              img="/images/products/assorted/cables/noco-cigarette-port-splitter.webp"
              title="Cigarette Port Splitter"
              description={
                <Fragment>
                  A splitter lets you turn one cigarette port into two or three.
                  This is the simplest option, but the total current is still
                  limited to about 10A across all ports.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
        </ul>
        <p>
          For a fully customized install when using a fuse block, consider
          getting a vibration-resistant, locking-style{" "}
          <Link
            affiliate="https://link.amazon/B0d4eaDPk"
            href="https://www.amazon.com/dp/B07H1MGWFN"
          >
            wired cigarette socket
          </Link>{" "}
          or{" "}
          <Link
            affiliate="https://link.amazon/B0bSDW0lv"
            href="https://www.amazon.com/dp/B0CNCHDN71"
          >
            wired cigarette socket panel
          </Link>
          .
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Adding an Anderson Powerpole Port</SlugHeading>
        <p>
          If your EcoFlow only has a cigarette port, you can still add an
          Anderson Powerpole connection using adapters or a fuse panel. Just
          keep in mind that when you start from a cigarette port, you’re still
          limited by its current rating and plug style.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/4gZDTWV"
              href="https://www.amazon.com/dp/B09PDD3R7J"
              img="/images/products/assorted/cables/cigarette-plug-to-anderson-powerpole.webp"
              title="Cigarette Port to Powerpole Adapter"
              description={
                <Fragment>
                  An adapter can convert your cigarette port into an Anderson
                  Powerpole plug. This works for low‑draw devices but is limited
                  by the cigarette port’s 10A maximum.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/3VsbLoc"
              href="https://www.amazon.com/dp/B0BJK66LPL"
              img="/images/products/assorted/cables/anderson-powerpole-to-ring-terminals.webp"
              title="Fuse Panel to Powerpole"
              description={
                <Fragment>
                  If you’re using a fuse panel, you can add an Anderson
                  Powerpole connection directly from the panel. This gives you a
                  cleaner plug.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
        </ul>
        <p>
          For a fully customized install when using a fuse block, consider
          getting a{" "}
          <Link
            affiliate="https://link.amazon/B0j17L4he"
            href="https://www.amazon.com/dp/B0DR85SPJG"
          >
            Powerpole connector set with crimping tool
          </Link>{" "}
          or a{" "}
          <Link
            affiliate="https://link.amazon/B03PyaOmZ"
            href="https://www.amazon.com/dp/B0DXKHRFLK"
          >
            wired Powerpole socket
          </Link>
          .
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Adding USB Ports</SlugHeading>
        <p>
          While EcoFlow power stations come with built-in USB ports, you may
          need additional USB-A or fast-charging USB-C outlets. You can easily
          add extra USB charge ports using your 12V outputs.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B08VuXwxv"
              href="https://www.amazon.com/dp/B0BPH6S4DN"
              img="/images/products/assorted/cables/anker-323.webp"
              title="Compact Cigarette Port to USB Adapter"
              description={
                <Fragment>
                  A 52.5W dual-port adapter featuring a 30W USB-C PD port and a
                  22.5W USB-A port to fast-charge phones, tablets, and small
                  accessories.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B0hPO0WpQ"
              href="https://www.amazon.com/dp/B0BDLC7RVB"
              img="/images/products/assorted/cables/lisen-b0bdlc7rvb.webp"
              title="Ultra-compact Cigarette Port to USB Adapter"
              description={
                <Fragment>
                  An ultra-compact dual-port adapter featuring a 36W USB-C PD
                  port and a 33W USB-A port that sits completely flush in your
                  12V socket to save space and prevent accidental snags.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
        </ul>
        <p>
          For a fully customized install when using a fuse block, consider
          getting a{" "}
          <Link
            affiliate="https://link.amazon/B065LGqXo"
            href="https://www.amazon.com/dp/B0BX6F1ZP9"
          >
            wired USB socket
          </Link>{" "}
          or a{" "}
          <Link
            affiliate="https://link.amazon/B04Qd5veP"
            href="https://www.amazon.com/dp/B0CY5RB88V"
          >
            wired USB socket panel
          </Link>
          .
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">DC5521 Barrel Jack</SlugHeading>
        <p>
          A few EcoFlow power stations also feature a{" "}
          <strong>DC5521 barrel jack</strong>. These outputs use a standard
          5.5mm outer diameter and 2.1mm inner pin configuration with
          center-positive polarity. They are typically rated for 3A to 5A per
          port (approx. 36W–60W at 12.6V), making them ideal for low-draw,
          stationary accessories such as Wi-Fi routers, security cameras, LED
          lights, and other small electronics.
        </p>

        <PhotoListItem
          affiliate="https://link.amazon/B045Z1znN"
          href="https://www.amazon.com/dp/B0BLYMVWNP"
          img="/images/products/assorted/cables/dc5521-male-90-deg.webp"
          title="DC5521 Barrel Plug"
          titleSuffix="(90 degree)"
          description={
            <Fragment>
              A 16AWG right-angle pigtail cable for hardwiring low-draw 12V
              accessories to your DC5521 port in tight spaces.
            </Fragment>
          }
          buttonText="Amazon"
          buttonIcon="Amazon"
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Powering DC Air Conditioners</SlugHeading>
        <p>
          Running a <strong>DC air conditioner</strong> from an EcoFlow power
          station requires careful attention to power limits. Built-in 12V DC
          ports cannot handle the heavy surge and running draws of most air
          conditioners. You can run an AC unit off your EcoFlow's 120V AC
          inverter outlets, but converting DC battery power to AC introduces
          energy efficiency losses. If you need dedicated off-grid cooling, get
          an <strong>EcoFlow WAVE</strong> AC unit or invest in a more capable{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/power-kits-collection"
          >
            EcoFlow Power Kit
          </Link>{" "}
          system.
        </p>
        <PhotoListItem
          affiliateData={affiliateMerchants.ecoflow}
          href="https://us.ecoflow.com/products/ecoflow-wave-3-portable-air-conditioner-with-heater?variant=42093673021513"
          img="/images/products/ecoflow/wave/wave-3/top-front-right.webp"
          title="EcoFlow WAVE 3"
          description={
            <Fragment>
              Portable air conditioner connects directly to compatible DELTA
              power stations. Delivers 6,100 BTU cooling and 6,800 BTU heating.
            </Fragment>
          }
          buttonText="EcoFlow"
          buttonIcon="EcoFlow"
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">EcoFlow Power Station Comparison</SlugHeading>
        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="Shop EcoFlow"
        />
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "Models with 12V DC output",
              content: <DataView data={groupedData.powerStationsDC} />,
            },
            {
              heading: "Models without 12V DC output",
              content: <DataView data={groupedData.powerStationsNoDC} />,
            },
            {
              heading: "EcoFlow Power Kit models",
              content: <DataView data={groupedData.powerkits} />,
            },
          ]}
        />

        <p>Frequently Asked Questions:</p>
        <Accordion
          headingsAs="h2"
          items={[
            {
              heading: "EcoFlow 12V Wiring FAQs",
              content: (
                <dl class={editorialStyle.noColon}>
                  <div>
                    <dt>
                      Which EcoFlow models have an Anderson Powerpole port?
                    </dt>
                    <dd>
                      <ProductLink brand="EcoFlow" model="DELTA Pro 3" />,{" "}
                      <ProductLink brand="EcoFlow" model="DELTA Pro" />,{" "}
                      <ProductLink brand="EcoFlow" model="DELTA 3 Ultra Plus" />
                      , <ProductLink brand="EcoFlow" model="DELTA 3 Max Plus" />
                      , <ProductLink
                        brand="EcoFlow"
                        model="DELTA Pro Ultra"
                      />{" "}
                      (non-X). See the{" "}
                      <SlugLink text="Anderson Powerpole Port with Fuse Block">
                        model list above
                      </SlugLink>{" "}
                      for details.
                    </dd>
                  </div>
                  <div>
                    <dt>Which EcoFlow models have a cigarette port?</dt>
                    <dd>
                      Most power station models with the exception of:{" "}
                      <ProductLink brand="EcoFlow" model="DELTA 3 Classic" />
                      , <ProductLink brand="EcoFlow" model="DELTA 3 1000 Air" />
                      , <ProductLink
                        brand="EcoFlow"
                        model="DELTA Pro 3"
                      />,{" "}
                      <ProductLink brand="EcoFlow" model="DELTA Pro Ultra" />,
                      and{" "}
                      <ProductLink brand="EcoFlow" model="DELTA Pro Ultra X" />.
                      See the{" "}
                      <SlugLink text="12V Cigarette Port with Fuse Block">
                        model list above
                      </SlugLink>{" "}
                      for details.
                    </dd>
                  </div>
                  <div>
                    <dt>Does the XT60 port output 12V power?</dt>
                    <dd>
                      No. The XT60 (XT60i) port is strictly for
                      solar/alternator/DC input. It does not provide any 12V
                      output.
                    </dd>
                  </div>
                  <div>
                    <dt>Can EcoFlow run 12V accessories while charging?</dt>
                    <dd>
                      Yes. EcoFlow supports using the 12V outputs while the unit
                      is charging, as long as total load stays within its
                      limits.
                    </dd>
                  </div>
                  <div>
                    <dt>Can EcoFlow run AC and DC at the same time?</dt>
                    <dd>
                      Yes. You can output AC and DC simultaneously as long as
                      the combined load stays within total capacity. AC and DC
                      outputs can be toggled on or off independently on the
                      physical unit or inside the app.
                    </dd>
                  </div>
                  <div>
                    <dt>What is the maximum current on EcoFlow 12V ports?</dt>
                    <dd>
                      EcoFlow Anderson Powerpole ports support up to 30A total.
                      Cigarette ports typically support 10A.
                    </dd>
                  </div>
                  <div>
                    <dt>Do I need to ground the fuse panel?</dt>
                    <dd>
                      No. The fuse block should only connect directly to the
                      positive and negative terminals on your EcoFlow output. Do
                      not ground the negative side to a vehicle chassis.
                    </dd>
                  </div>
                  <div>
                    <dt>Can I run a 12V fridge from the cigarette port?</dt>
                    <dd>
                      Yes, as long as the fridge draws less than the port limit.
                      Many 12V fridges draw 3A to 6A, which is within the
                      cigarette port range. Using a DC fridge is typically much
                      more efficient than an AC fridge, which requires the
                      inverter to always run.
                    </dd>
                  </div>
                  <div>
                    <dt>Can I run a diesel heater from an EcoFlow?</dt>
                    <dd>
                      Yes, but it is best operated from a high-current 30A
                      Anderson Powerpole port. Diesel heaters create a high
                      initial amp draw during glow-plug startup, which
                      frequently trips the protection limits on 10A cigarette
                      ports.
                    </dd>
                  </div>
                </dl>
              ),
            },
          ]}
        />

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>

      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.batteryCompatibility} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="Fuse Block + Cable"
        description="Anderson & Cigarette Options"
        options={[
          {
            text: "View Products",
            icon: "Amazon",
            // secondary: true,
            options: [
              {
                text: "Fuse Block + Anderson Powerpole Cable",
                affiliate: "https://link.amazon/B04cMwcvd",
                href: "https://www.amazon.com/shop/matanich/photo/amzn1.shoppablemedia.v1.collage.0296f7f0-f7d2-40b2-a91d-e9e759b9c193?tag=matanich-20",
              },
              {
                text: "Fuse Block + Cigarette Port Cable",
                affiliate: "https://link.amazon/B0hcWH54y",
                href: "https://www.amazon.com/shop/matanich/photo/amzn1.shoppablemedia.v1.collage.f0e7827a-0e8d-45e0-8829-a6752c421c95?tag=matanich-20",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
